import { ArrowRight, Sparkles, Shield, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";

const Hero = () => {
  const scrollToBooking = (e) => {
    e.preventDefault();
    const element = document.querySelector("#booking");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 hero-bg opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/40" />
      
      {/* Decorative elements */}
      <div className="absolute top-40 right-20 w-72 h-72 bg-[#2563EB]/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#3B82F6]/10 rounded-full blur-2xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="animate-slide-in-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#3B82F6]/10 text-[#2563EB] px-4 py-2 rounded-full mb-6">
              <Leaf className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-medium">Eco-Friendly Cleaning</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              A Cleaner Home,
              <br />
              <span className="gradient-text">A Happier Life</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-slate-600 mb-8 max-w-lg">
              Professional residential and commercial cleaning services in Northern New Brunswick. 
              Let us handle the dirt so you can enjoy the sparkle.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                onClick={scrollToBooking}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-semibold btn-glow transition-all group"
                data-testid="hero-get-quote-btn"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full px-8 py-6 text-lg font-medium"
                data-testid="hero-view-services-btn"
              >
                View Services
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-slate-600">
                <div className="w-10 h-10 bg-[#2563EB]/10 rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                </div>
                <span className="text-sm font-medium">500+ Happy Clients</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                </div>
                <span className="text-sm font-medium">100% Satisfaction</span>
              </div>
            </div>
          </div>

          {/* Right Content - Featured Image */}
          <div className="relative animate-slide-in-right hidden lg:block">
            <div className="relative">
              {/* Main Image */}
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_fresh-sweep-7/artifacts/8a5uid7i_1000013409.jpg"
                  alt="Clean modern kitchen"
                  className="w-full h-[500px] object-cover"
                  data-testid="hero-image"
                />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 z-20 bg-white rounded-2xl shadow-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 eco-badge rounded-xl flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-accent text-lg text-[#2563EB]">Eco-Friendly</p>
                    <p className="text-xs text-slate-500">Green certified products</p>
                  </div>
                </div>
              </div>

              {/* Background decoration */}
              <div className="absolute -top-4 -right-4 w-full h-full bg-[#2563EB]/10 rounded-3xl -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
